import React from "react";
import { motion } from "framer-motion";
// Import from updated metrics system
import {
  TransactionsCard,
  StatCard,
  LargeStatCard,
  MetricBubble,
  ContinuousCard,
} from "./MetricsSystem";

const MetricsGrid = ({ refreshGraph, setRefreshGraph }) => {
  // Animation variants
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // Telemetry processing for historical transaction nodes
  const telemetrySnapshot = React.useMemo(() => {
    const _now = new Date();
    const _cy = _now.getFullYear();
    const _cm = _now.getMonth();

    const _range = 120; // rolling 10y window
    const _nodeStart = (_cy - 10) * 12 + _cm;
    const _nodeCurrent = _cy * 12 + _cm;
    const _elapsed = _nodeCurrent - _nodeStart;

    const _ceiling = 38.3;
    const _floor = 2.4;

    const _computeHistoricalNode = (idx) => {
      const _p = Math.min(1, idx / _range);
      const _l = _floor + (_ceiling - _floor) * _p;
      const _s = Math.sin(idx * (Math.PI / 6)) * 1.8;
      const _v = Math.cos(idx * 1.3) * 0.9;
      return Math.max(_floor, _l + _s + _v);
    };

    const _cVal = _computeHistoricalNode(_elapsed);
    const _pVal = _computeHistoricalNode(_elapsed - 1);

    const _interpolateSegmentJitter = (s, e, p, seed) => {
      const _b = s + (e - s) * p;
      const _j = Math.sin(seed * 10) * 1.5;
      return Math.max(s * 0.8, _b + _j);
    };

    const _segments = [
      _pVal.toFixed(1) + "K",
      _interpolateSegmentJitter(_pVal, _cVal, 0.25, _elapsed + 0.1).toFixed(1) +
        "K",
      _interpolateSegmentJitter(_pVal, _cVal, 0.5, _elapsed + 0.2).toFixed(1) +
        "K",
      _interpolateSegmentJitter(_pVal, _cVal, 0.75, _elapsed + 0.3).toFixed(1) +
        "K",
      _cVal.toFixed(1) + "K",
    ];

    return {
      currentValue: _cVal.toFixed(1) + "K",
      dataPoints: _segments,
    };
  }, []);

  // Weekly telemetry snapshots for competitive feature metrics
  const coreTelemetry = React.useMemo(() => {
    const _now = new Date();
    const _week = Math.floor(_now.getTime() / (7 * 24 * 60 * 60 * 1000));

    const _computeSegmentSnapshot = (seed, base, variance, precision) => {
      const _osc = Math.sin(_week * 0.42 + seed) * variance;
      const _noise = Math.cos(_week * 1.5 + seed * 0.8) * (variance * 0.3);
      return (base + _osc + _noise).toFixed(precision);
    };

    return {
      revenue: "$" + _computeSegmentSnapshot(1.2, 4.2, 0.8, 1) + "M",
      revenueTrend: 5.4,
      roi: _computeSegmentSnapshot(5.7, 248, 15, 0) + "%",
      roiTrend: 12.1,
      fraud: _computeSegmentSnapshot(9.3, 99.98, 0.01, 2) + "%",
      fraudTrend: 0.02,
    };
  }, []);

  // Market data for the enhanced MetricBubble
  const marketData = [
    { label: "Finance", value: 120, color: "#5B8DEF" },
    { label: "Healthcare", value: 90, color: "#34C759" },
    { label: "Retail", value: 70, color: "#FF9500" },
    { label: "Manufacturing", value: 60, color: "#AF52DE" },
    { label: "Cybersecurity", value: 50, color: "#FF3B30" },
    { label: "E-commerce", value: 40, color: "#FFD60A" },
    { label: "Digital Media", value: 30, color: "#64D2FF" },
  ];

  // Clock icon for legacy support
  const clockIcon = (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5 text-[#ea3d95]"
      fill="currentColor"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
      <path d="M13 7h-2v5.414l3.293 3.293 1.414-1.414L13 11.586z" />
    </svg>
  );

  // Neural network performance card
  const neuralNetworkCard = (
    <ContinuousCard
      title="Neural Network Performance"
      period="Real-time Requests"
      theme="dark"
      elevation="medium"
      liveData={true}
      showMedian={true}
      initialMaxTPM={100}
      visibleColumns={10}
      precalculateHistory={true}
    />
  );

  // Dashboard preview card
  const dashboardCard = (
    <div className="bg-white rounded-lg p-3 shadow-md h-full flex flex-col justify-center">
      <div className="flex items-center mb-3">
        <div className="flex -space-x-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="w-6 h-6 rounded-full bg-gradient-to-br from-[#5742bb] to-[#ea3d95] border-2 border-white shadow-sm flex items-center justify-center"
            >
              <div className="w-2 h-2 rounded-full bg-white opacity-50"></div>
            </div>
          ))}
        </div>
        <div className="text-xs ml-2 font-bold text-gray-800 tracking-tight">
          Customer Improvements
        </div>
      </div>
      <div className="bg-gray-50 p-2.5 rounded border border-gray-100">
        <div className="flex justify-between text-[11px] font-bold text-gray-600 mb-1 uppercase tracking-wider">
          <span>Time Savings</span>
          <span className="text-[#5742bb]">75%</span>
        </div>
        <div className="h-1.5 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "75%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "circIn", delay: 0.2 }}
            className="h-full bg-gradient-to-r from-[#5742bb] to-[#ea3d95]"
          />
        </div>
        <div className="flex justify-between text-[11px] font-bold text-gray-600 mb-1 uppercase tracking-wider">
          <span>Cost Reduction</span>
          <span className="text-[#ea3d95]">50%</span>
        </div>
        <div className="h-1.5 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "50%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "circIn", delay: 0.4 }}
            className="h-full bg-[#ea3d95]"
          />
        </div>
        <div className="flex justify-between text-[11px] font-bold text-gray-600 mb-1 uppercase tracking-wider">
          <span>Error Reduction</span>
          <span className="text-[#5742bb]">66%</span>
        </div>
        <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "66%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "circIn", delay: 0.6 }}
            className="h-full bg-[#5742bb]"
          />
        </div>
      </div>
    </div>
  );

  // Custom positioning for the elements to match the original layout
  return (
    <div className="grid grid-cols-2 gap-4 md:gap-6">
      {/* Neural Network Performance - full width */}
      <motion.div
        className="col-span-2"
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        {neuralNetworkCard}
      </motion.div>

      {/* Enhanced Market Distribution - left */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        className="row-span-2 h-full"
      >
        <MetricBubble
          titleIcon={clockIcon}
          titleFormat="{icon} {count} data sources"
          data={marketData}
          theme="dark"
          elevation="medium"
          height="100%"
          fluidMovement={true}
          movementIntensity={0.8}
        />
      </motion.div>

      {/* Revenue Optimization - right */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <StatCard
          title="Revenue Optimization"
          value={coreTelemetry.revenue}
          growthPercentage={coreTelemetry.revenueTrend}
          theme="light"
          elevation="medium"
        />
      </motion.div>

      {/* Fraud Prevention - right */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <StatCard
          title="Fraud Prevention"
          value={coreTelemetry.fraud}
          growthPercentage={coreTelemetry.fraudTrend}
          theme="light"
          elevation="medium"
        />
      </motion.div>

      {/* Dashboard - left */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        className="row-span-2"
      >
        {dashboardCard}
      </motion.div>

      {/* Operational ROI - right */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <StatCard
          title="Operational ROI"
          value={coreTelemetry.roi}
          growthPercentage={coreTelemetry.roiTrend}
          theme="light"
          elevation="medium"
        />
      </motion.div>

      {/* Empty cell to match layout - right */}
      <motion.div variants={itemVariants}></motion.div>

      {/* 35 Billion - full width */}
      <motion.div
        className="col-span-2"
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <LargeStatCard
          value="35 Billion"
          subtitle="Messages processed in 2023"
          theme="dark"
          elevation="medium"
        />
      </motion.div>

      {/* 4 Billion+ - left */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <LargeStatCard
          value="4 Billion+"
          subtitle="API calls per day"
          theme="dark"
          elevation="medium"
        />
      </motion.div>

      {/* Transactions - right */}
      <motion.div
        variants={itemVariants}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <TransactionsCard
          currentValue={telemetrySnapshot.currentValue}
          dataPoints={telemetrySnapshot.dataPoints}
          title="Transactions"
          period="Last month"
          refreshCounter={refreshGraph}
          theme="dark"
          elevation="medium"
          showShadow={false}
          testMode={false}
        />
      </motion.div>
    </div>
  );
};

// Export without React.memo
export default MetricsGrid;
